import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import {
  GlobalLoaderService,
  LoaderContent,
  LoaderState,
} from '@services/core-services/global-loader.service';
import { GlobalLoaderComponent } from '@enginuity/core/organisms/global-loader/global-loader.component';
import { User } from '@services/core-services/models';
import { AuthService } from '@services/auth-services/auth.service';
import { SSEComponent } from '@enginuity/core/organisms/sse/sse.component';
import { CurrencyPipe } from '@angular/common';
import { ShortcutsService } from '@services/core-services/shortcuts.service';
import { GlobalCommandsService } from '@services/core-services/global-command.service';
import { Observable, Subscription, timer } from 'rxjs';
import { OverlayLayoutComponent } from '@enginuity/core/layouts/overlay-layout/overlay-layout.component';
import { OverlayValueDataComponent } from '@enginuity/core/organisms/overlay-value-data/overlay-value-data.component';
import { MatDialog } from '@angular/material/dialog';
import { take, switchMap } from 'rxjs/operators';
import { NgxSonnerToaster } from 'ngx-sonner';

interface ShortcutCategory {
  name: string;
  shortcuts: { name: string; shortcut: string }[];
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    GlobalLoaderComponent,
    SSEComponent,
    OverlayLayoutComponent,
    OverlayValueDataComponent,
    NgxSonnerToaster,
  ],
  providers: [CurrencyPipe],
})
export class AppComponent implements OnInit, OnDestroy {
  protected readonly LoaderState = LoaderState;
  protected loaderState: LoaderState = LoaderState.None;
  protected loaderContent: LoaderContent | undefined = undefined;
  protected user: User | undefined = undefined;
  showShortcutsOverlay$: Observable<boolean>;
  shortcutCategories: ShortcutCategory[] = [];

  private subscriptions = new Subscription();

  constructor(
    private readonly loaderService: GlobalLoaderService,
    private readonly authService: AuthService,
    private shortcutsService: ShortcutsService,
    private globalCommandsService: GlobalCommandsService,
    private dialog: MatDialog
  ) {
    this.subscriptions.add(
      this.loaderService.getContent().subscribe(content => (this.loaderContent = content))
    );
    this.subscriptions.add(
      this.loaderService.getStatus().subscribe(status => (this.loaderState = status))
    );
    this.subscriptions.add(this.authService.getUser().subscribe(user => (this.user = user)));
    this.showShortcutsOverlay$ = this.shortcutsService.showShortcutsOverlay$;

    // Listen for global shortcut commands
    this.subscriptions.add(
      this.globalCommandsService.shortcutTriggered.subscribe(command => {
        if (command === 'show-shortcuts') {
          this.shortcutsService.showShortcuts();
        } else if (command === 'searchCustomers') {
          this.openSearchWithContext('Customers');
        } else if (command === 'searchOrders') {
          this.openSearchWithContext('Orders');
        } else if (command === 'searchProducts') {
          this.openSearchWithContext('Products');
        }
      })
    );
  }

  ngOnInit() {
    this.shortcutCategories = this.shortcutsService.getShortcutCategories();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onShortcutsOverlayClose() {
    this.shortcutsService.hideShortcuts();
  }

  private openSearchWithContext(context: string) {
    // First open the search modal
    this.globalCommandsService.shortcutTriggered.emit('openSearch');

    // Wait for the next tick to ensure the modal opening command has been processed,
    // then emit the context event. This is more reliable than a fixed timeout.
    timer(0)
      .pipe(
        take(1),
        switchMap(() => timer(50)) // Small delay to ensure modal is rendered
      )
      .subscribe(() => {
        this.globalCommandsService.shortcutTriggered.emit(`openSearch-${context}`);
      });
  }

  // Global shortcuts are now handled by GlobalCommandsService
  // No need for manual keyboard event handling here
}
